import axios from "axios";
import secureLocalStorage from "react-secure-storage";

// Retrieve the base URL from secure local storage.
const baseURL = secureLocalStorage.getItem("baseURL");
const authData = JSON.parse(localStorage.getItem("kt-auth-react-v") || "{}")
const accessToken = authData?.access_token
// ==========================================================================
// APPOINTMENT CRUD & LOGS (using Axios)
// ==========================================================================

/**
 * Fetches all appointments.
 * @returns {Promise<Array>} A promise that resolves to an array of appointments.
 */
export const get_all_appointments = async () => {
    let response = await axios.get(baseURL + "/api/appointment");
    return response.data;
};

/**
 * Deletes a specific appointment by its ID.
 * @param {number} id - The ID of the appointment to delete.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const delete_appointment = async (id: number) => {
    let response = await axios.delete(baseURL + "/api/appointment/" + id);
    return response.data;
};

/**
 * Deletes a specific people-appointment by its ID.
 * @param {number} id - The ID of the people-appointment to delete.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const delete_people_appointment = async (id: number) => {
    let response = await axios.delete(baseURL + "/api/people-appointment/" + id);
    return response.data;
};

/**
 * Retrieves a single appointment by its ID.
 * @param {number} id - The ID of the appointment to fetch.
 * @returns {Promise<object>} A promise that resolves to the single appointment data.
 */
export const get_single_appointment = async (id: number) => {
    let response = await axios.get(baseURL + "/api/appointment/" + id);
    return response.data;
};

/**
 * Retrieves a single people-appointment by its ID.
 * @param {number} id - The ID of the people-appointment to fetch.
 * @returns {Promise<object>} A promise that resolves to the single people-appointment data.
 */
export const get_single_people_appointment = async (id: number) => {
    let response = await axios.get(baseURL + "/api/people-appointment/" + id);
    return response.data;
};

/**
 * Fetches the activity logs for a single appointment.
 * @param {number} id - The ID of the appointment.
 * @returns {Promise<Array>} A promise that resolves to an array of logs.
 */
export const get_appointment_logs = async (id: number) => {
    let response = await axios.get(baseURL + "/api/single-appointment-log/" + id);
    return response.data;
};

/**
 * Fetches the activity logs for a single people-appointment.
 * @param {number} id - The ID of the people-appointment.
 * @returns {Promise<Array>} A promise that resolves to an array of logs.
 */
export const get_people_appointment_logs = async (id: number) => {
    let response = await axios.get(baseURL + "/api/people-appointment-log/" + id);
    return response.data;
};

/**
 * Fetches all appointment logs from the system.
 * @returns {Promise<Array>} A promise that resolves to an array of all logs.
 */
export const get_all_appointments_logs = async () => {
    let response = await axios.get(baseURL + "/api/all-appointment-logs");
    return response.data;
};

/**
 * Fetches appointments based on a set of filters.
 * @param {number} per_page - Number of items per page.
 * @param {number} page - The current page number.
 * @param {number|null} peopleId - ID of the person to filter by.
 * @param {string|null} fromDate - Start date for the date range filter.
 * @param {string|null} toDate - End date for the date range filter.
 * @param {string|null} appointmentStatus - Status of the appointment (e.g., 'confirmed').
 * @param {string|null} paymentStatus - Status of the payment (e.g., 'paid').
 * @param {string|null} appointment_for - The context of the appointment.
 * @returns {Promise<object>} A promise that resolves to the filtered appointment data.
 */
export const get_filtered_appointments = async (per_page, page, peopleId, fromDate, toDate, appointmentStatus, paymentStatus, appointment_for) => {
    const response = await axios.post(baseURL + '/api/filtered-appointment', {
        per_page,
        page,
        people_flag: peopleId ? "specific" : "",
        people_id: peopleId,
        date_flag: fromDate ? "specific" : "",
        from_date: fromDate,
        to_date: toDate,
        appointment_flag: appointmentStatus ? "specific" : "",
        appointment_status: appointmentStatus,
        payment_flag: paymentStatus ? "specific" : "",
        payment_status: paymentStatus,
        appointment_for: appointment_for
    });
    return response.data;
};

/**
 * Fetches all appointments associated with a specific person.
 * @param {number} peopleId - The ID of the person.
 * @returns {Promise<object>} A promise that resolves to the appointment data for that person.
 */
export const get_appointments_against_person = async (peopleId) => {
    const response = await axios.post(baseURL + '/api/filtered-appointment', {
        people_flag: "specific",
        people_id: peopleId,
        // Other filters are left empty to fetch all for this person.
        date_flag: "",
        from_date: "",
        to_date: "",
        appointment_flag: "",
        appointment_status: "",
        payment_flag: "",
        payment_status: ""
    });
    return response.data;
};

/**
 * Changes the status of a specific appointment.
 * @param {number} appointmentId - The ID of the appointment to update.
 * @param {string} appointmentStatus - The new status to set.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const change_appointment_status = async (appointmentId: number, appointmentStatus) => {
    const response = await axios.post(baseURL + '/api/change-appointment-status', {
        appointment_id: appointmentId,
        appointment_status: appointmentStatus,
    });
    return response.data;
};

// ==========================================================================
// APPOINTMENT NOTES (using Axios)
// ==========================================================================

/**
 * Fetches all notes for a given appointment ID.
 * @param {number} appointmentId - The ID of the appointment.
 * @returns {Promise<Array>} A promise that resolves to an array of notes.
 */
export const get_all_appointment_notes = async (appointmentId: number) => {
    let response = await axios.post(baseURL + "/api/appointment-notes-against-id", {
        appointment_id: appointmentId,
        appointment_flag: "specific",
        status_flag: "all",
        status: 1
    });
    return response.data;
};

/**
 * Deletes a specific appointment note by its ID.
 * @param {number} id - The ID of the note to delete.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const delete_appointment_note = async (id: number) => {
    let response = await axios.delete(baseURL + "/api/appointment-notes/" + id);
    return response.data;
};

/**
 * Updates an existing appointment note.
 * @param {number} id - The ID of the note to update.
 * @param {number} appointmentId - The ID of the associated appointment.
 * @param {string} AppointmentNotes - The new content of the note.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const update_appointemnt_note = async (id: number, appointmentId: number, AppointmentNotes: string) => {
    let response = await axios.patch(baseURL + "/api/appointment-notes/" + id, {
        appointment_id: appointmentId,
        appointment_notes: AppointmentNotes,
        status: 1
    });
    return response.data;
};

/**
 * Adds a new note to an appointment.
 * @param {number} appointmentId - The ID of the appointment to add the note to.
 * @param {string} AppointmentNotes - The content of the new note.
 * @returns {Promise<object>} A promise that resolves to the API response data.
 */
export const add_appointemnt_note = async (appointmentId: number, AppointmentNotes: string) => {
    let response = await axios.post(baseURL + "/api/appointment-notes/", {
        appointment_id: appointmentId,
        appointment_notes: AppointmentNotes,
        status: 1
    });
    return response.data;
};

// ==========================================================================
// APPOINTMENT BOOKING WIDGET (using Fetch API)
// ==========================================================================

// Common headers for Fetch API requests.
const header = {
    'Content-Type': 'application/json',
    // If you have an authorization token, add it here.
     Authorization: `Bearer ${accessToken}`, 
};

/**
 * Fetches available locations based on the appointment type.
 * @param {number} typeValue - The type of appointment (0 for office, 1 for virtual).
 * @returns {Promise<Array>} A promise that resolves to an array of location options formatted for react-select.
 */
export const fetchLocationData = async (typeValue) => {
    const payload = {
        status: 1,
        type: typeValue == 0 ? 'meeting_at_office' : 'virtual'
    };
    try {
        const response = await fetch(`${baseURL}/api/get-locations`, {
            method: 'POST',
            headers: header,
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error('Network response was not ok for locations');
        }
        const data = await response.json();
        // Transform the API data to the { value, label } format for react-select.
        return data.data.map(location => ({
            value: location.id,
            label: location.platform_name
        }));
    } catch (error) {
        console.error('Fetch operation problem (locations):', error);
        throw error;
    }
};

/**
 * Fetches the main categories for appointments.
 * @returns {Promise<Array>} A promise that resolves to an array of category options formatted for react-select.
 */
export const fetchCategoryData = async () => {
    try {
        const response = await fetch(`${baseURL}/api/categories/1`, {
            method: 'GET',
            headers: header,
        });
        if (!response.ok) {
            throw new Error('Network response was not ok for categories');
        }
        const data = await response.json();
        // Transform the API data to the { value, label } format for react-select.
        return data.data.map(category => ({
            value: category.id,
            label: category.title
        }));
    } catch (error) {
        console.error('Fetch operation problem (categories):', error);
        throw error;
    }
};

/**
 * Fetches sub-categories based on the selected main category.
 * @param {number} categoryId - The ID of the selected parent category.
 * @returns {Promise<Array>} A promise that resolves to an array of sub-category options formatted for react-select.
 */
export const fetchSubcategoryData = async (categoryId) => {
    const payload = {
        status: 1,
        categories_id: categoryId
    };
    try {
        const response = await fetch(`${baseURL}/api/get-sub-categories`, {
            method: 'POST',
            headers: header,
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error('Network response was not ok for sub-categories');
        }
        const data = await response.json();
        // Transform the API data to the { value, label } format for react-select.
        return data.data.map(subCategory => ({
            value: subCategory.id,
            label: subCategory.title
        }));
    } catch (error) {
        console.error('Fetch operation problem (sub-categories):', error);
        throw error;
    }
};

/**
 * Fetches available people based on location and sub-category.
 * @param {number} locationId - The ID of the selected location.
 * @param {number} subCategoryId - The ID of the selected sub-category.
 * @returns {Promise<Array>} A promise that resolves to an array of people data.
 */
export const fetchPeopleData = async (locationId, subCategoryId) => {
    const payload = {
        status: 1,
        location_id: locationId,
        sub_category_id: subCategoryId
    };
    try {
        const response = await fetch(`${baseURL}/api/get-people-against-subcategory-location`, {
            method: 'POST',
            headers: header,
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error('Network response was not ok for people data');
        }
        const data = await response.json();
        return data.data; // Return the full data array for people cards.
    } catch (error) {
        console.error('Fetch operation problem (people):', error);
        throw error;
    }
};

/**
 * Fetches the booked time slots for a specific person.
 * @param {number} peopleId - The ID of the selected person.
 * @returns {Promise<Array>} A promise that resolves to an array of booked date-time strings.
 */
export const fetchSlotData = async (peopleId) => {
    try {
        const fullURL = `${baseURL}/api/get-time-slots/${peopleId}`;
        const response = await fetch(fullURL, {
            method: 'GET',
            headers: header,
        });

        if (!response.ok) {
            console.error("Server responded with an error:", response.status, response.statusText);
            throw new Error('Network response was not ok for time slots');
        }

        const data = await response.json();
        // Return the complete data object including appointment_date_time, holidays, and schedule
        return data.data || {};
    } catch (error) {
        console.error("CRITICAL: Error inside fetchSlotData's try-catch block:", error);
        throw error; // Propagate the error to be handled in the component.
    }
};

/**
 * Submits the final appointment details and initiates payment.
 * On success, it redirects the user to the Stripe payment URL.
 * @param {object} payload - The complete appointment data object.
 */
export const bookAppointment = async (payload) => {
    try {
        // Save the current URL to local storage for returning after payment.
        localStorage.setItem('bookingRedirectUrl', window.location.href);

        if (!accessToken) {
            throw new Error("Access token not found. Please login again.")
        }
        const response = await fetch(`${baseURL}/api/appointment`, {
            method: 'POST',
            headers: header,
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json(); // Get error message from the server.
            throw new Error(errorData.message || 'Network response was not ok');
        }

        const data = await response.json();

        // Redirect to the Stripe URL provided by the API.
        if (data.data && data.data[0] && data.data[0].stripe_url) {
            window.location.href = data.data[0].stripe_url;
        } else {
            throw new Error("Stripe URL not found in API response.");
        }

    } catch (error) {
        console.error('There was a problem with the booking operation:', error);
        throw error; // Propagate the error to be handled in the component.
    }
};

/**
 * Fetches the addon settings for the booking widget.
 * @returns {Promise<object>} A promise that resolves to a parsed settings object.
 */
export const fetchSettingData = async () => {
    try {
        const response = await fetch(`${baseURL}/api/get-addon-settings`, {
            method: 'GET',
            headers: header,
        });
        if (!response.ok) {
            throw new Error('Network response was not ok for settings');
        }
        const data = await response.json();

        // The API returns a stringified JSON object, so it needs to be parsed.
        if (data.data && data.data[0] && data.data[0].setting_value) {
            return JSON.parse(data.data[0].setting_value);
        }

        // Return an empty object as a fallback if settings are not found.
        return {};

    } catch (error) {
        console.error('There was a problem with the fetch settings operation:', error);
        throw error; // Propagate the error to be handled in the component.
    }
};